/**
 * NOLAS 2026, (c) Mikaël FOURNY.
 * src/pwa/versionWatch.ts
 *
 * Rôle :
 *   Détecter qu'une NOUVELLE VERSION de l'application est en ligne, même sans
 *   service worker (navigation privée, navigateur qui le refuse, console
 *   e.labriquerie.net qui n'en a pas). La version, c'est l'empreinte du
 *   fichier d'entrée (`assets/index-XXXX.js`) référencé par `index.html` :
 *   elle change à chaque build. On relit `index.html` en contournant les
 *   caches, on compare à celle qui a chargé la page ; si elle diffère, on
 *   affiche le bandeau « l'application a évolué ».
 *
 * Quand : à chaque retour sur l'onglet (visibilitychange) espacé d'au moins
 * une minute, et toutes les 5 minutes pendant que l'onglet est visible
 * (rythme du 2026-10-04 ; c'était une heure). Jamais
 * plus souvent : le serveur n'est pas interrogé à chaque bascule d'onglet.
 *
 * Ce module complète registerServiceWorker.ts, il ne le remplace pas : avec
 * un service worker en mode prompt, c'est lui qui signale ; sans, c'est ici.
 * Le bandeau est le même, avec sa garde anti-doublon.
 *
 * Mikael - chef de projet - 2026-09-20
 */
import { afficherBandeauMiseAJour } from './updateBanner';
import { activerOuSignaler } from './activationOuverture';
import { rechargerVersionRecente } from './rechargerVersionRecente';

// 2026-10-04 (Mika : « l'application doit signaler qu'il faut recharger à
// chaque mise à jour, comme EPS Manager ») : à une heure, un déploiement du
// matin restait invisible toute la matinée. On relit index.html (quelques
// Ko) toutes les 5 minutes tant que l'onglet est visible, et au retour sur
// l'onglet dès qu'une minute s'est écoulée. Mikael - chef de projet
const INTERVALLE_MS = 5 * 60 * 1000;
const DELAI_MIN_MS = 60 * 1000;
const MOTIF_ENTREE = /assets\/index-[A-Za-z0-9_-]+\.js/;

/** L'empreinte de l'entrée qui a chargé la page courante, ou null. */
export function empreinteChargee(doc: Document = document): string | null {
  for (const script of Array.from(doc.scripts)) {
    const m = MOTIF_ENTREE.exec(script.src);
    if (m) return m[0];
  }
  return null;
}

/** L'empreinte de l'entrée référencée par un `index.html`. */
export function empreinteDansHtml(html: string): string | null {
  const m = MOTIF_ENTREE.exec(html);
  return m ? m[0] : null;
}

/**
 * Relit index.html et compare. Retourne true si une nouvelle version est en
 * ligne. Silencieux en cas d'erreur réseau : on réessaiera.
 */
export async function nouvelleVersionEnLigne(): Promise<boolean> {
  const locale = empreinteChargee();
  if (locale === null) return false;
  try {
    const res = await fetch(new URL('index.html', document.baseURI).href, { cache: 'reload', credentials: 'same-origin' });
    if (!res.ok) return false;
    const distante = empreinteDansHtml(await res.text());
    return distante !== null && distante !== locale;
  } catch {
    return false;
  }
}

/**
 * Réaction par défaut à une nouvelle version détectée sans service worker :
 * aux premiers instants (ouverture ou retour au premier plan, sans geste ni
 * saisie, file hors ligne vide), un seul rechargement ; sinon le bandeau.
 * Mikael - chef de projet - 2026-10-04
 */
export function reagirNouvelleVersion(): void {
  // 2026-10-04 : jamais un simple location.reload() : sous l'ancien service
  // worker, il ressert l'ancienne version (voir rechargerVersionRecente.ts).
  // Mikael - chef de projet - 2026-10-04
  const recharger = (): void => {
    void rechargerVersionRecente();
  };
  void activerOuSignaler({
    activer: recharger,
    signaler: () => afficherBandeauMiseAJour(recharger),
  });
}

/** Démarre la surveillance. Retourne une fonction d'arrêt (tests). */
export function surveillerLaVersion(surNouvelleVersion: () => void = reagirNouvelleVersion): () => void {
  let derniereVerification = Date.now();
  let signale = false;
  const verifier = async (): Promise<void> => {
    if (signale) return;
    derniereVerification = Date.now();
    if (await nouvelleVersionEnLigne()) {
      signale = true;
      surNouvelleVersion();
    }
  };
  const surVisibilite = (): void => {
    if (document.visibilityState === 'visible' && Date.now() - derniereVerification >= DELAI_MIN_MS) {
      void verifier();
    }
  };
  document.addEventListener('visibilitychange', surVisibilite);
  // Comme EPS Manager (pwa/versionApplication.js) : aussi au retour du focus
  // sur la fenêtre (application installée, bascule entre fenêtres).
  window.addEventListener('focus', surVisibilite);
  const minuteur = window.setInterval(() => {
    if (document.visibilityState === 'visible') void verifier();
  }, INTERVALLE_MS);
  return () => {
    document.removeEventListener('visibilitychange', surVisibilite);
    window.removeEventListener('focus', surVisibilite);
    window.clearInterval(minuteur);
  };
}
